import { encodeDnsQuery } from "./dnsWire";
import { aggregateResult } from "./stats";
import type {
  BenchmarkOptions,
  BenchmarkProgress,
  DoHMethod,
  QueryResult,
  Resolver,
  ResolverResult
} from "./types";

type BenchmarkEvents = {
  onProgress: (progress: BenchmarkProgress) => void;
  onResult: (result: ResolverResult) => void;
};

type RequestCapability = {
  method: DoHMethod;
  opaque: boolean;
};

type MethodCache = Map<string, RequestCapability>;

export class BenchmarkRunner {
  private activeControllers = new Set<AbortController>();
  private capabilityCache: MethodCache = new Map();
  private cancelled = false;
  private running = false;
  private completedResolvers = 0;
  private completedQueries = 0;
  private startedAt = 0;

  isRunning(): boolean {
    return this.running;
  }

  cancel(): void {
    this.cancelled = true;
    for (const controller of this.activeControllers) controller.abort();
  }

  async run(
    resolvers: Resolver[],
    hostnames: string[],
    options: BenchmarkOptions,
    events: BenchmarkEvents
  ): Promise<ResolverResult[]> {
    if (this.running) throw new Error("A benchmark is already running.");
    this.running = true;
    this.cancelled = false;
    this.completedResolvers = 0;
    this.completedQueries = 0;
    this.startedAt = performance.now();

    const results = new Map<string, ResolverResult>();
    const queue = [...resolvers];
    const totalResolvers = resolvers.length;
    const totalQueries = resolvers.length * hostnames.length;

    const emitProgress = (currentResolver?: string, currentHostname?: string) => {
      events.onProgress({
        currentResolver,
        currentHostname,
        completedResolvers: this.completedResolvers,
        totalResolvers,
        completedQueries: this.completedQueries,
        totalQueries,
        elapsedMs: performance.now() - this.startedAt,
        running: this.running,
        cancelled: this.cancelled
      });
    };

    emitProgress();

    const worker = async () => {
      while (queue.length && !this.cancelled) {
        const resolver = queue.shift();
        if (!resolver) return;
        const result = await this.testResolver(resolver, hostnames, options, emitProgress);
        results.set(resolver.id, result);
        this.completedResolvers += 1;
        events.onResult(result);
        emitProgress(resolver.name);
      }
    };

    const workers = Array.from(
      { length: Math.max(1, Math.min(options.resolverConcurrency, resolvers.length)) },
      worker
    );
    await Promise.all(workers);
    this.running = false;
    emitProgress();

    return resolvers
      .map((resolver) => results.get(resolver.id))
      .filter((result): result is ResolverResult => Boolean(result));
  }

  private async testResolver(
    resolver: Resolver,
    hostnames: string[],
    options: BenchmarkOptions,
    emitProgress: (resolver?: string, hostname?: string) => void
  ): Promise<ResolverResult> {
    let result: ResolverResult = {
      resolverId: resolver.id,
      resolverName: resolver.name,
      endpoint: resolver.endpoint,
      status: "warming",
      queryResults: []
    };
    emitProgress(resolver.name, "warm-up");

    const warmup = await this.request(resolver, hostnames[0], options.timeoutMs, true);
    if (this.cancelled) return { ...result, status: "cancelled" };
    if (warmup.status !== "success" && warmup.status !== "opaque") {
      const status = warmup.status === "cors_blocked" ? "cors_blocked" : "unavailable";
      return aggregateResult({ ...result, status, queryResults: [warmup] });
    }

    result = { ...result, status: "testing" };
    for (const hostname of hostnames) {
      if (this.cancelled) {
        result = { ...result, status: "cancelled" };
        break;
      }
      emitProgress(resolver.name, hostname);
      const query = await this.request(resolver, hostname, options.timeoutMs, false);
      result.queryResults.push(query);
      this.completedQueries += 1;
      result = aggregateResult(result);
      emitProgress(resolver.name, hostname);
    }

    if (result.status !== "cancelled") {
      const hasSuccess = result.queryResults.some(
        (query) => query.status === "success" || query.status === "opaque"
      );
      const allTimeout = result.queryResults.every((query) => query.status === "timeout");
      const allCors = result.queryResults.every((query) => query.status === "cors_blocked");
      result = {
        ...aggregateResult(result),
        status: hasSuccess
          ? "complete"
          : allCors
            ? "cors_blocked"
            : allTimeout
              ? "timed_out"
              : "unavailable"
      };
    }

    return result;
  }

  private async request(
    resolver: Resolver,
    hostname: string,
    timeoutMs: number,
    allowFallback: boolean
  ): Promise<QueryResult> {
    const cached = this.capabilityCache.get(resolver.id);
    if (cached?.opaque) {
      return timedOpaqueRequest(resolver.endpoint, hostname, timeoutMs, this.activeControllers);
    }

    const methods = cached
      ? [cached.method]
      : methodOrder(resolver.preferredMethod ?? "AUTO", allowFallback);

    let lastResult: QueryResult | undefined;
    for (const method of methods) {
      const query = await timedRequest(
        resolver.endpoint,
        hostname,
        method,
        timeoutMs,
        this.activeControllers
      );
      if (query.status === "success") {
        this.capabilityCache.set(resolver.id, { method, opaque: false });
        return query;
      }
      lastResult = query;
      if (query.status === "cancelled" || query.status === "timeout") break;
    }

    if (lastResult?.status === "cors_blocked" || lastResult?.status === "error") {
      const opaque = await timedOpaqueRequest(
        resolver.endpoint,
        hostname,
        timeoutMs,
        this.activeControllers
      );
      if (opaque.status === "opaque") {
        this.capabilityCache.set(resolver.id, { method: "GET", opaque: true });
        return opaque;
      }
      lastResult = opaque;
    }

    return (
      lastResult ?? { hostname, status: "error", errorMessage: "No request method was attempted." }
    );
  }
}

function methodOrder(preferred: "GET" | "POST" | "AUTO", allowFallback: boolean): DoHMethod[] {
  if (preferred === "GET") return allowFallback ? ["GET", "POST"] : ["GET"];
  if (preferred === "POST") return allowFallback ? ["POST", "GET"] : ["POST"];
  return allowFallback ? ["GET", "POST"] : ["GET"];
}

export async function timedRequest(
  endpoint: string,
  hostname: string,
  method: DoHMethod,
  timeoutMs: number,
  activeControllers = new Set<AbortController>()
): Promise<QueryResult> {
  const controller = new AbortController();
  activeControllers.add(controller);
  let timedOut = false;
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const started = performance.now();

  try {
    const response = await fetch(buildRequest(endpoint, hostname, method, controller.signal));
    const durationMs = performance.now() - started;
    if (!response.ok) {
      return {
        hostname,
        status: "error",
        durationMs,
        methodUsed: method,
        errorMessage: `HTTP ${response.status} ${response.statusText}`.trim()
      };
    }
    await response.arrayBuffer();
    return { hostname, status: "success", durationMs, methodUsed: method };
  } catch (error) {
    const aborted = controller.signal.aborted;
    const errorMessage = error instanceof Error ? error.message : "Network request failed.";
    if (aborted) {
      return {
        hostname,
        status: timedOut ? "timeout" : "cancelled",
        methodUsed: method,
        errorMessage: "The request was aborted."
      };
    }
    return {
      hostname,
      status: classifyCors(errorMessage),
      methodUsed: method,
      errorMessage
    };
  } finally {
    window.clearTimeout(timeout);
    activeControllers.delete(controller);
  }
}

export async function timedOpaqueRequest(
  endpoint: string,
  hostname: string,
  timeoutMs: number,
  activeControllers = new Set<AbortController>()
): Promise<QueryResult> {
  const controller = new AbortController();
  activeControllers.add(controller);
  let timedOut = false;
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const started = performance.now();

  try {
    await fetch(buildOpaqueRequest(endpoint, hostname, controller.signal));
    return {
      hostname,
      status: "opaque",
      durationMs: performance.now() - started,
      methodUsed: "GET",
      opaque: true,
      errorMessage: "Opaque no-cors request completed; DNS response body was not readable."
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Network request failed.";
    if (controller.signal.aborted) {
      return {
        hostname,
        status: timedOut ? "timeout" : "cancelled",
        methodUsed: "GET",
        errorMessage: "The opaque request was aborted."
      };
    }
    return {
      hostname,
      status: classifyCors(errorMessage),
      methodUsed: "GET",
      errorMessage
    };
  } finally {
    window.clearTimeout(timeout);
    activeControllers.delete(controller);
  }
}

function buildRequest(
  endpoint: string,
  hostname: string,
  method: DoHMethod,
  signal: AbortSignal
): Request {
  if (method === "GET") {
    const url = new URL(endpoint);
    if (url.hostname === "dns.google" && url.pathname === "/resolve") {
      url.searchParams.set("name", hostname);
      url.searchParams.set("type", "A");
    } else {
      url.searchParams.set("name", hostname);
      url.searchParams.set("type", "A");
    }
    return new Request(url, {
      method: "GET",
      headers: { Accept: "application/dns-json" },
      signal
    });
  }

  return new Request(endpoint, {
    method: "POST",
    headers: {
      Accept: "application/dns-message",
      "Content-Type": "application/dns-message"
    },
    body: encodeDnsQuery(hostname).buffer as ArrayBuffer,
    signal
  });
}

function buildOpaqueRequest(endpoint: string, hostname: string, signal: AbortSignal): Request {
  const url = new URL(endpoint);
  if (url.pathname.includes("/resolve")) {
    url.searchParams.set("name", hostname);
    url.searchParams.set("type", "A");
  } else {
    url.searchParams.set("dns", encodeDnsQueryBase64Url(encodeDnsQuery(hostname)));
  }
  return new Request(url, {
    method: "GET",
    mode: "no-cors",
    signal
  });
}

function encodeDnsQueryBase64Url(query: Uint8Array): string {
  let binary = "";
  for (const byte of query) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function classifyCors(message: string): QueryResult["status"] {
  return /failed to fetch|load failed|networkerror|cors|cross-origin|not allowed/i.test(message)
    ? "cors_blocked"
    : "error";
}
